import { post, request } from '../api';

/** Mirrors the views of `apps/api/src/knowledge/*` (ADR-0010). */

export const FINDING_KINDS = ['pain', 'need', 'objection', 'quote', 'idea', 'question'] as const;
export type FindingKind = (typeof FINDING_KINDS)[number];
export type FindingStatus = 'proposed' | 'confirmed' | 'rejected';
export type FunnelStatus = 'collecting' | 'complete' | 'needs_human';
export type SourceKind = 'call_transcript' | 'video' | 'lesson' | 'extract';
export type SourceState = 'awaiting_transcript' | 'awaiting_analysis' | 'analyzed';

type Ref = { id: string; name: string } | null;
export interface KnowledgeFile { id: string; filename: string; mimeType: string; sizeBytes: number; path: string }

export interface Finding {
  id: string;
  kind: FindingKind;
  text: string;
  context: string | null;
  segment: string | null;
  tags: string[];
  status: FindingStatus;
  business: Ref;
  source: { id: string; title: string; kind: SourceKind } | null;
  sourceAt: string | null;
  funnelCase: { id: string; title: string } | null;
  inboxItemId: string | null;
  sourceUrl: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export interface FunnelCase {
  id: string;
  title: string;
  niche: string | null;
  product: string | null;
  owner: string | null;
  url: string | null;
  entryPlatform: string | null;
  entryText: string | null;
  entryFile: KnowledgeFile | null;
  offer: string | null;
  price: string | null;
  nextChannel: string | null;
  design: string | null;
  tags: string[];
  status: FunnelStatus;
  note: string | null;
  business: Ref;
  inboxItemId: string | null;
  stepCount: number;
  findingCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface FunnelDetail extends FunnelCase {
  panelUrl: string;
  steps: Array<{ id: string; order: number; seen: string; text: string | null; cta: string | null; leadsTo: string | null; url: string | null; file: KnowledgeFile | null }>;
  findings: Finding[];
}

export interface Source {
  id: string;
  kind: SourceKind;
  title: string;
  business: Ref;
  url: string | null;
  externalId: string | null;
  state: SourceState;
  language: string;
  tags: string[];
  version: number;
  findingCount: number;
  inboxItemId: string | null;
  createdAt: string;
  transcribedAt: string | null;
  analyzedAt: string | null;
}

export interface SourceDetail extends Source {
  metadata: Record<string, unknown>;
  panelUrl: string;
  text: { text: string; offset: number; nextOffset: number | null; length: number } | null;
  findings: Finding[];
}

export interface Page<T> { rows: T[]; total: number; page: number; pageSize: number }
export type FindingPage = Page<Finding> & { counts: Record<FindingStatus, number> };

export interface KnowledgeWeek {
  since: string;
  counts: { findings: number; proposed: number; funnels: number; sources: number };
  findings: Finding[];
  funnels: FunnelCase[];
  sources: Source[];
}

export interface ListParams { q?: string; businessId?: string; kind?: SourceKind[]; page: number; pageSize: number }

function query(p: ListParams): URLSearchParams {
  const q = new URLSearchParams({ page: String(p.page), pageSize: String(p.pageSize) });
  if (p.q) q.set('q', p.q);
  if (p.businessId) q.set('businessId', p.businessId);
  if (p.kind?.length) q.set('kind', p.kind.join(','));
  return q;
}

export const knowledgeApi = {
  week: (): Promise<KnowledgeWeek> => request('/knowledge/week'),
  findings: (p: ListParams): Promise<FindingPage> => request(`/knowledge/findings?${query(p)}`),
  finding: (id: string): Promise<Finding> => request(`/knowledge/findings/${encodeURIComponent(id)}`),
  decide: (id: string, status: 'confirmed' | 'rejected'): Promise<Finding> => post(`/knowledge/findings/${encodeURIComponent(id)}/decide`, { status }),
  funnels: (p: ListParams): Promise<Page<FunnelCase>> => request(`/knowledge/funnels?${query(p)}`),
  funnel: (id: string): Promise<FunnelDetail> => request(`/knowledge/funnels/${encodeURIComponent(id)}`),
  sources: (p: ListParams): Promise<Page<Source>> => request(`/knowledge/sources?${query(p)}`),
  source: (id: string, offset = 0): Promise<SourceDetail> => request(`/knowledge/sources/${encodeURIComponent(id)}?offset=${offset}&limit=30000`),
};
